import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/footer.scss";

const shopLinks = [
  { label: "Women", to: "/shop?audience=women" },
  { label: "Men", to: "/shop?audience=men" },
  { label: "Kids", to: "/shop?audience=kids" },
  { label: "Footwear", to: "/shop?category=footwear" },
  { label: "Equipment", to: "/shop?category=equipment" },
  { label: "Sale", to: "/shop?tag=sale" },
];

const helpLinks = [
  { label: "Contact us", to: "/contact" },
  { label: "Orders & cancellations", to: "/orders-cancellations" },
  { label: "Payments", to: "/payments-policy" },
  { label: "Delivery", to: "/shipping" },
  { label: "Returns", to: "/returns" },
  { label: "Warranty & faulty goods", to: "/warranty-policy" },
  { label: "Complaints", to: "/complaints-policy" },
  { label: "Shopping bag", to: "/cartpage" },
];

const companyLinks = [
  { label: "Our story", to: "/about" },
  { label: "Policy centre", to: "/policies" },
  { label: "Privacy", to: "/privacy" },
  { label: "Cookies", to: "/cookies" },
  { label: "Terms & conditions", to: "/terms" },
  { label: "Accessibility", to: "/accessibility" },
  { label: "Legal notice", to: "/legal-notice" },
  { label: "FAQs", to: "/faqs" },
];

const paymentMethods = ["VISA", "Mastercard", "PayPal", "Apple Pay", "Klarna"];

function formatThreshold(value) {
  try {
    return new Intl.NumberFormat(siteConfig.locale || "en-SG", {
      style: "currency",
      currency: siteConfig.currency || "USD",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${siteConfig.currency || "USD"} ${value}`;
  }
}

function getRegionLabel() {
  const locale = siteConfig.locale || "en-SG";
  const regionCode =
    siteConfig.countryCode || locale.split("-")[1]?.toUpperCase() || "SG";

  try {
    return (
      new Intl.DisplayNames([locale], { type: "region" }).of(regionCode) ||
      regionCode
    );
  } catch {
    return regionCode;
  }
}

function scrollToDocumentStart() {
  const reduceMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
}

function FooterServiceIcon({ title }) {
  const serviceName = title.toLowerCase();

  if (serviceName.includes("return")) {
    return (
      <svg aria-hidden="true" viewBox="0 0 48 48">
        <path d="M10 17a16 16 0 1 1-1 13" />
        <path d="m4 11 6 6 7-6" />
      </svg>
    );
  }

  if (
    serviceName.includes("delivery") &&
    !serviceName.includes("member")
  ) {
    return (
      <svg aria-hidden="true" viewBox="0 0 48 48">
        <path d="M4 12h25v22H4zM29 19h7l8 8v7H29z" />
        <circle cx="13" cy="37" r="4" />
        <circle cx="36" cy="37" r="4" />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 48 48">
      <path d="M24 4 40 10v12c0 10-6 18-16 22C14 40 8 32 8 22V10z" />
      <path d="m17 24 5 5 10-11" />
    </svg>
  );
}

function FooterLinkColumn({ title, links, onNavigate }) {
  return (
    <nav className="site-footer__column" aria-label={title}>
      <h2>{title}</h2>
      <ul>
        {links.map((link) => (
          <li key={link.label}>
            <Link to={link.to} onClick={onNavigate}>
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Footer() {
  const regionLabel = getRegionLabel();

  const fallbackServices = [
    {
      title: `Tracked delivery ${formatThreshold(siteConfig.shippingCost || 9.99)}`,
      text: "Tracked delivery direct to your door.",
    },
    {
      title: "Free and easy returns",
      text: "Send unworn items back within 30 days.",
    },
    {
      title: "Built for the outdoors",
      text: "Dependable gear backed by expert support.",
    },
  ];

  const configuredServices = (siteConfig.services || []).filter(
    (service) => service && service.title
  );
  const services = [...configuredServices, ...fallbackServices].slice(0, 3);

  const socialLinks = Object.entries(siteConfig.social || {}).filter(
    ([, url]) => typeof url === "string" && /^https?:\/\//i.test(url)
  );

  const toTop = () => {
    scrollToDocumentStart();
  };

  return (
    <footer className="site-footer" role="contentinfo">
      <section className="site-footer__services" aria-label="Customer services">
        {services.map((service, index) => (
          <article className="site-footer__service" key={`${service.title}-${index}`}>
            <FooterServiceIcon title={service.title} />
            <div>
              <h2>{service.title}</h2>
              {service.text && <p>{service.text}</p>}
            </div>
          </article>
        ))}
      </section>

      <div className="site-footer__main">
        <div className="site-footer__grid">
          <div className="site-footer__brand-block">
            <Link
              className="site-footer__brand"
              to="/"
              onClick={toTop}
              aria-label={`${siteConfig.brandName} home`}
            >
              {siteConfig.logo && (
                <img src={siteConfig.logo} alt="" aria-hidden="true" />
              )}
              <span>{siteConfig.brandName}</span>
            </Link>
            <p>{siteConfig.description}</p>
            {socialLinks.length > 0 && (
              <div className="site-footer__social" aria-label="Social media">
                {socialLinks.map(([name, url]) => (
                  <a
                    href={url}
                    key={name}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${siteConfig.brandName} on ${name}`}
                  >
                    {name.slice(0, 1).toUpperCase()}
                  </a>
                ))}
              </div>
            )}
          </div>

          <FooterLinkColumn
            title="Shop"
            links={shopLinks}
            onNavigate={toTop}
          />
          <FooterLinkColumn
            title="Help & support"
            links={helpLinks}
            onNavigate={toTop}
          />
          <FooterLinkColumn
            title="About us"
            links={companyLinks}
            onNavigate={toTop}
          />

          <section className="site-footer__newsletter" aria-labelledby="support-title">
            <p className="site-footer__eyebrow">Customer care</p>
            <h2 id="support-title">Questions about an order?</h2>
            <p>Our support team can help with products, delivery and returns.</p>
            <address>
              {siteConfig.contact?.email && (
                <a href={`mailto:${siteConfig.contact.email}`}>
                  {siteConfig.contact.email}
                </a>
              )}
              {siteConfig.contact?.hours && <span>{siteConfig.contact.hours}</span>}
              {siteConfig.contact?.address && <span>{siteConfig.contact.address}</span>}
            </address>
          </section>
        </div>

        <div className="site-footer__payments" aria-label="Accepted payments">
          <span>Secure payments</span>
          <ul>
            {paymentMethods.map((method) => (
              <li key={method}>{method}</li>
            ))}
          </ul>
        </div>

        <div className="site-footer__bottom">
          <p>
            © {new Date().getFullYear()} {siteConfig.brandName}. All rights reserved.
          </p>
          <nav aria-label="Legal">
            <Link to="/privacy" onClick={toTop}>
              Privacy
            </Link>
            <Link to="/terms" onClick={toTop}>
              Terms
            </Link>
            <Link to="/returns" onClick={toTop}>
              Returns
            </Link>
            <Link to="/cookies" onClick={toTop}>Cookies</Link>
            <Link to="/accessibility" onClick={toTop}>Accessibility</Link>
            <Link to="/legal-notice" onClick={toTop}>Legal notice</Link>
            <Link to="/faqs" onClick={toTop}>FAQs</Link>
            <Link to="/shipping" onClick={toTop}>
              Delivery
            </Link>
          </nav>
          <span>
            {regionLabel} · {siteConfig.currency || "USD"}
          </span>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
